<template>
    <form
        v-bind="$attrs"
        ref="formRef"
        @submit.prevent
    >
        <slot></slot>
    </form>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import useFlash from '@/Shared/Hook/useFlash'
    import {
        FlashStatusTypeEnum,
        ResponseJsonKeyEnum,
    } from '@/Shared/AutoGenerated/Main'
    import ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'

    const props = defineProps({
        method: {
            type: String as PropType<'POST' | 'GET'>,
            default: 'POST',
        },
        url: {
            type: String,
            required: true,
        },
        disableCatch: {
            type: Boolean,
            default: true,
        },
    })

    const formRef = ref<HTMLFormElement | null>(null)
    const loading = ref<Boolean>(false)
    const response = ref<ApiFormResponseType | null>(null)

    const submit = () => {
        loading.value = false

        if (formRef.value === null) {
            throw new Error('FormRef should not be null.')
        }

        if (isInvalid()) {
            reportInvalid()
            return
        }

        loading.value = true
        const formData = new FormData(formRef.value)

        return new Promise(
            (resolve: (value: ApiFormResponseType) => void, reject) => {
                const request = new XMLHttpRequest()

                request.onreadystatechange = function () {
                    if (this.readyState === XMLHttpRequest.DONE) {
                        const json = JSON.parse(
                            this.response ? this.response : '{}',
                        )
                        const apiFormResponse = new ApiFormResponseType(json)
                        response.value = apiFormResponse

                        loading.value = false

                        // Redirect
                        if (this.status === 302) {
                            const redirectUrl =
                                json?.payload[
                                    ResponseJsonKeyEnum.RedirectUrl
                                ] ?? null

                            if (!redirectUrl) {
                                throw new Error('Missing redirect URL.', {
                                    cause: json,
                                })
                            }

                            window.location.assign(redirectUrl)

                            return
                        }

                        // Validation error
                        if (this.status === 422 && props.disableCatch) {
                            return
                        } else if (this.status === 422) {
                            reject(apiFormResponse)

                            return
                        }

                        const message = json?.message
                        const hasMessage =
                            typeof message === 'string' && message.length > 0
                        const isSuccess =
                            this.status >= 200 && this.status < 300

                        // Success
                        if (isSuccess && hasMessage) {
                            useFlash().display({
                                type: FlashStatusTypeEnum.Success,
                                message: message,
                            })
                        }

                        if (isSuccess) {
                            resolve(apiFormResponse)

                            return
                        }

                        // Error
                        if (hasMessage) {
                            useFlash().display({
                                type: FlashStatusTypeEnum.Error,
                                message: message,
                            })
                        }

                        if (props.disableCatch) {
                            return
                        }

                        reject(apiFormResponse)
                    }
                }

                request.open(props.method, props.url, true)
                request.setRequestHeader('Accept', 'application/json')
                request.send(formData)
            },
        )
    }

    const isInvalid = (): boolean => {
        if (formRef.value === null) {
            throw new Error('FormRef should not be null.')
        }

        return formRef.value.checkValidity() !== true
    }

    const reportInvalid = (): boolean => {
        if (formRef.value === null) {
            throw new Error('FormRef should not be null.')
        }

        return formRef.value.reportValidity()
    }

    defineExpose({
        submit,
        isInvalid,
        reportInvalid,
        loading,
        response,
    })
</script>
